import { useState } from 'react';
import { Download, Loader2, Upload, ArrowLeft, Check } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { api, apiError } from '@/lib/api';
import { ContactImportMapping } from './ContactImportMapping';
import { ContactImportReview } from './ContactImportReview';
import { downloadContactFile } from './contactDownloads';

export const ContactImportDialog = ({ onClose, onImported }) => {
  const [file, setFile] = useState(null);
  const [inspection, setInspection] = useState(null);
  const [mapping, setMapping] = useState({});
  const [delimiter, setDelimiter] = useState('auto');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [commitAttempted, setCommitAttempted] = useState(false);
  const completed = preview?.state === 'completed';
  const formData = (selected, separator) => { const body = new FormData(); body.append('file', selected); body.append('delimiter', separator); return body; };

  const inspect = async (selected, separator) => {
    setFile(selected || null); setInspection(null); setMapping({}); setError('');
    if (!selected) return;
    if (!/\.csv$/i.test(selected.name)) { setError('Bitte eine CSV-Datei auswählen.'); return; }
    if (selected.size > 2*1024*1024) { setError('Die CSV-Datei darf höchstens 2 MB groß sein.'); return; }
    setBusy('inspect');
    try { const { data } = await api.post('/admin/contact-import/inspect', formData(selected, separator)); setInspection(data); setMapping(data.suggested_mapping); }
    catch (e) { setError(apiError(e)); } finally { setBusy(''); }
  };
  const review = async () => {
    setBusy('preview'); setError('');
    try {
      const body = formData(file, delimiter); body.append('mapping', JSON.stringify(mapping));
      const { data } = await api.post('/admin/contact-import/preview', body); setPreview(data);
    } catch (e) { setError(apiError(e)); } finally { setBusy(''); }
  };
  const commit = async () => {
    setBusy('commit'); setError(''); setCommitAttempted(true);
    try { const { data } = await api.post(`/admin/contact-import/${preview.id}/commit`, { confirmed: true }); setPreview(data); setFile(null); onImported(data); }
    catch (e) { setError(`${apiError(e)} Erneutes Bestätigen ist sicher: Bereits angelegte Kontakte werden nicht doppelt importiert.`); }
    finally { setBusy(''); }
  };
  const download = async (report) => {
    setBusy('download'); setError('');
    try { await downloadContactFile(report ? `/admin/contact-import/${preview.id}/report.csv` : '/admin/contact-import/template.csv', report ? 'kontakt-import-bericht.csv' : 'kontakt-import-vorlage.csv'); }
    catch (e) { setError(apiError(e)); } finally { setBusy(''); }
  };
  const back = async () => {
    setBusy('back'); setError('');
    try { await api.delete(`/admin/contact-import/${preview.id}`); setPreview(null); }
    catch (e) { setError(apiError(e)); } finally { setBusy(''); }
  };
  const close = () => {
    if (busy) return;
    if (preview && !completed && !commitAttempted) api.delete(`/admin/contact-import/${preview.id}`).catch(() => {});
    onClose();
  };
  return <Dialog open onOpenChange={value => { if (!value) close(); }}>
    <DialogContent data-testid="contact-import-dialog" className="bg-[#0d0d0d] border-white/20 text-white w-[calc(100%-2rem)] max-w-4xl max-h-[88dvh] flex flex-col overflow-hidden p-5 sm:p-6">
      <div className="shrink-0 pr-6">
        <DialogTitle data-testid="contact-import-title" className="font-heading text-lg uppercase">{completed ? 'Import abgeschlossen' : 'Kontakte aus CSV importieren'}</DialogTitle>
        <DialogDescription data-testid="contact-import-consent-notice" className="text-neutral-400 text-xs mt-2">Vorhandene E-Mail-Adressen werden übersprungen. Importierte Kontakte werden automatisch als Newsletter-Empfänger (Status „Angemeldet“) übernommen; bestehende Abmeldungen bleiben erhalten.</DialogDescription>
      </div>
      <div data-testid="contact-import-steps" className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-neutral-500 shrink-0">
        <span className={!preview ? 'text-white' : ''}>1 · Datei & Spalten</span><span className={preview && !completed ? 'text-white' : ''}>2 · Prüfen & bestätigen</span><span className={completed ? 'text-white' : ''}>3 · Ergebnis</span>
      </div>
      <div className="min-h-0 overflow-y-auto pr-1" data-testid="contact-import-scroll">
        {preview ? <ContactImportReview preview={preview} /> : <ContactImportMapping file={file} inspection={inspection} mapping={mapping} setMapping={setMapping} delimiter={delimiter} busy={!!busy} onFile={selected => inspect(selected, delimiter)} onDelimiter={value => { setDelimiter(value); if (file) inspect(file, value); }} />}
      </div>
      {error && <p role="alert" data-testid="contact-import-error" className="text-sm text-red-400 break-words shrink-0">{error}</p>}
      {busy && <p role="status" data-testid="contact-import-busy" className="text-xs text-neutral-400 flex items-center gap-2"><Loader2 size={14} className="animate-spin" />{busy === 'commit' ? 'Kontakte werden importiert …' : 'Wird verarbeitet …'}</p>}
      <div className="flex flex-wrap gap-2 justify-between border-t border-white/15 pt-4 shrink-0">
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" disabled={!!busy} data-testid={preview ? 'contact-import-report' : 'contact-import-template'} onClick={() => download(!!preview)} className="bg-transparent border-white/20 hover:bg-white/10 text-xs gap-2"><Download size={14} />{preview ? 'CSV-Bericht' : 'CSV-Vorlage'}</Button>
          {preview && !completed && !commitAttempted && <Button variant="ghost" disabled={!!busy} data-testid="contact-import-back" onClick={back} className="text-xs gap-1"><ArrowLeft size={14} />Zuordnung ändern</Button>}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" disabled={!!busy} data-testid="contact-import-cancel" onClick={close} className="bg-transparent border-white/20 text-xs">{completed ? 'Schließen' : 'Abbrechen'}</Button>
          {!preview && <Button disabled={!!busy || !inspection || mapping.email === undefined} data-testid="contact-import-preview" onClick={review} className="bg-[#ff0033] hover:bg-[#cc0029] text-white text-xs gap-2"><Upload size={14} />Vorschau prüfen</Button>}
          {preview && !completed && <Button disabled={!!busy || preview.summary.ready === 0} data-testid="contact-import-confirm" onClick={commit} className="bg-[#ff0033] hover:bg-[#cc0029] text-white text-xs gap-2"><Check size={14} />{preview.summary.ready} neue Kontakte importieren</Button>}
        </div>
      </div>
    </DialogContent>
  </Dialog>;
};